import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Zap, Trophy, Users, Clock, Check, X, Play, Square } from 'lucide-react';
import { getSocket } from '../lib/socket';
import { useAuth } from '../lib/store';
import { Avatar } from './ui';
import toast from 'react-hot-toast';

export default function TriviaChatCard({ groupId }) {
  const { user } = useAuth();
  const [live, setLive] = useState(null);      // {state,index,total,question}
  const [selected, setSelected] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [board, setBoard] = useState([]);
  const [answered, setAnswered] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [finalBoard, setFinalBoard] = useState(null);
  const tick = useRef();

  useEffect(() => {
    const s = getSocket(); if (!s || !groupId) return;
    s.emit('trivia:live:state', { groupId }, (st) => {
      if (st?.running) {
        setLive(st);
        if (st.question) startTimer(st.question.endsAt);
        setBoard(st.leaderboard || []);
        if (st.alreadyAnswered) setSelected(-1);
      }
    });

    const onStart = (d) => { setLive({ state: 'countdown', ...d }); setFinalBoard(null); setBoard([]); toast('🧠 Live Trivia starting!', { icon: '⚡' }); };
    const onQ = (qd) => { setLive(l => ({ ...(l || {}), state: 'question', question: qd, index: qd.index, total: qd.total })); setSelected(null); setReveal(null); setAnswered(0); startTimer(qd.endsAt); };
    const onAns = ({ count }) => setAnswered(count);
    const onReveal = (d) => { setReveal(d); setBoard(d.leaderboard || []); clearInterval(tick.current); };
    const onFin = (d) => { setFinalBoard(d.results); setLive(null); setReveal(null); clearInterval(tick.current); };
    const onCancel = () => { setLive(null); setReveal(null); clearInterval(tick.current); };

    s.on('trivia:live:started', onStart);
    s.on('trivia:live:question', onQ);
    s.on('trivia:live:answered', onAns);
    s.on('trivia:live:reveal', onReveal);
    s.on('trivia:live:finished', onFin);
    s.on('trivia:live:cancelled', onCancel);
    return () => {
      s.off('trivia:live:started', onStart); s.off('trivia:live:question', onQ);
      s.off('trivia:live:answered', onAns); s.off('trivia:live:reveal', onReveal);
      s.off('trivia:live:finished', onFin); s.off('trivia:live:cancelled', onCancel);
      clearInterval(tick.current);
    };
  }, [groupId]);

  function startTimer(endsAt) {
    clearInterval(tick.current);
    const upd = () => setTimeLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    upd(); tick.current = setInterval(upd, 250);
  }

  function answer(i) {
    if (selected !== null || reveal) return;
    setSelected(i);
    getSocket()?.emit('trivia:live:answer', { groupId, index: i }, (r) => {
      if (r?.error) { toast.error(r.error); setSelected(null); }
    });
    if (navigator.vibrate) navigator.vibrate(15);
  }

  function start() {
    getSocket()?.emit('trivia:live:start', { groupId }, (r) => {
      if (r?.error) toast.error(r.error);
    });
  }

  /* ---------- FINAL PODIUM ---------- */
  if (finalBoard) {
    return (
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="card p-4 bg-gradient-to-br from-amber-400 via-brand-500 to-grape-600 text-white mb-2">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={18} /><p className="font-display font-extrabold">Live Trivia results</p>
        </div>
        <div className="space-y-1.5">
          {finalBoard.slice(0, 5).map((r, i) => (
            <motion.div key={r.userId} initial={{ x: -18, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
              transition={{ delay: i * 0.08 }}
              className={`flex items-center gap-2.5 p-2 rounded-xl ${r.userId === user.id ? 'bg-white/25' : 'bg-white/12'}`}>
              <span className="text-lg w-6">{['🥇', '🥈', '🥉'][i] || `#${i + 1}`}</span>
              <Avatar src={r.avatar} name={r.username} size={28} />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-xs truncate">@{r.username}</p>
                <p className="text-[10px] opacity-80">{r.correct} correct · {r.firsts} first 🥇</p>
              </div>
              <span className="font-black text-sm">+{r.total}💎</span>
            </motion.div>))}
        </div>
        <button onClick={() => setFinalBoard(null)} className="btn bg-white/20 text-white btn-sm w-full mt-3">Close</button>
      </motion.div>
    );
  }

  /* ---------- IDLE ---------- */
  if (!live) {
    return (
      <button onClick={start}
        className="w-full card p-3 mb-2 flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-grape-600 text-white active:scale-[.98] transition">
        <div className="w-9 h-9 rounded-xl bg-white/20 grid place-items-center"><Brain size={18} /></div>
        <div className="flex-1 text-left">
          <p className="font-bold text-sm">Start Live Trivia</p>
          <p className="text-[11px] opacity-80">Everyone in this chat plays · type <b>!trivia</b></p>
        </div>
        <Play size={16} />
      </button>
    );
  }

  /* ---------- COUNTDOWN ---------- */
  if (live.state === 'countdown') {
    return (
      <motion.div initial={{ scale: .9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="card p-5 mb-2 bg-gradient-to-br from-indigo-600 to-grape-700 text-white text-center">
        <motion.div animate={{ scale: [1, 1.2, 1], rotate: [0, -8, 8, 0] }} transition={{ duration: 1, repeat: Infinity }}>
          <Brain size={36} className="mx-auto mb-2" />
        </motion.div>
        <p className="font-display text-xl font-extrabold">Live Trivia starting…</p>
        <p className="text-sm opacity-85">{live.total} questions · {live.seconds}s each</p>
      </motion.div>
    );
  }

  /* ---------- QUESTION / REVEAL ---------- */
  const qd = live.question;
  if (!qd) return null;
  const pct = (timeLeft / (qd.seconds || 15)) * 100;

  return (
    <motion.div layout className="card overflow-hidden mb-2 border-2 border-grape-400">
      <div className="p-3 bg-gradient-to-r from-indigo-600 to-grape-700 text-white">
        <div className="flex items-center justify-between text-[11px] font-black mb-1.5">
          <span className="flex items-center gap-1"><Brain size={11} /> LIVE Q{qd.index + 1}/{qd.total}</span>
          <span className="flex items-center gap-1"><Users size={10} /> {answered} answered</span>
          <span className={`flex items-center gap-1 tabular-nums ${timeLeft <= 4 ? 'text-red-300 animate-pulse' : ''}`}>
            <Clock size={10} /> {timeLeft}s</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/25 overflow-hidden">
          <motion.div className={`h-full ${pct < 30 ? 'bg-red-400' : pct < 60 ? 'bg-amber-400' : 'bg-mint-400'}`}
            animate={{ width: `${pct}%` }} transition={{ duration: .25, ease: 'linear' }} />
        </div>
      </div>

      <div className="p-3">
        <p className="font-bold text-sm mb-2.5">{qd.question}</p>
        <div className="grid grid-cols-2 gap-1.5">
          {qd.options.map((o, i) => {
            const isCorrect = reveal && i === reveal.correct_index;
            const isWrong = reveal && selected === i && i !== reveal.correct_index;
            const isSel = selected === i;
            return (
              <motion.button key={i} onClick={() => answer(i)} disabled={selected !== null || !!reveal}
                whileTap={{ scale: .94 }}
                className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left text-xs font-bold transition
                  ${isCorrect ? 'border-mint-500 bg-mint-400/15 text-mint-700'
                    : isWrong ? 'border-red-500 bg-red-50 text-red-600 animate-shake'
                    : isSel ? 'border-brand-500 bg-brand-50'
                    : 'border-ink-200 bg-white'}`}>
                <span className={`w-6 h-6 rounded-lg grid place-items-center font-black text-[10px] shrink-0
                  ${isCorrect ? 'bg-mint-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-ink-100 text-ink-500'}`}>
                  {isCorrect ? <Check size={12} /> : isWrong ? <X size={12} /> : 'ABCD'[i]}
                </span>
                <span className="flex-1 leading-tight">{o}</span>
              </motion.button>);
          })}
        </div>

        <AnimatePresence>
          {reveal && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              className="mt-2.5 p-2.5 rounded-xl bg-ink-50">
              {reveal.explanation && <p className="text-[11px] text-ink-600 font-semibold">{reveal.explanation}</p>}
              {reveal.results.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {reveal.results.slice(0, 6).map(r => (
                    <span key={r.userId} className={`chip ${r.first ? 'bg-amber-400 text-ink-900' : 'chip-mint'}`}>
                      {r.first && '🥇'}@{r.username} +{r.points}
                    </span>))}
                </div>)}
              {!reveal.results.length && <p className="text-[11px] text-ink-400 font-bold mt-1">Nobody got it 😅</p>}
            </motion.div>)}
        </AnimatePresence>

        {board.length > 0 && (
          <div className="mt-2.5 flex gap-1.5 overflow-x-auto no-scrollbar">
            {board.slice(0, 6).map((b, i) => (
              <div key={b.userId} className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg
                ${b.userId === user.id ? 'bg-brand-100' : 'bg-ink-50'}`}>
                <span className="text-[10px] font-black text-ink-400">#{i + 1}</span>
                <Avatar src={b.avatar} name={b.username} size={18} />
                <span className="text-[10px] font-black">{b.points}</span>
              </div>))}
          </div>)}
      </div>
    </motion.div>
  );
}